import React, { useCallback, useEffect, useState } from "react";
import {
  CircularProgress,
  Container,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import PersonAddAltIcon from "@mui/icons-material/PersonAddAlt";
import { useParams } from "react-router-dom";
import { GroupInvitation, GroupMember, GroupRole } from "../lib/types";
import { groupAPI } from "../services/api";
import {
  InviteMemberModal,
  MembersList,
  PendingInvitations,
  RemoveMemberConfirm,
} from "../components/groups";

const GroupMembersPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const inviteLogStorageKey = id ? `finance_invite_log_${id}` : null;
  const [groupName, setGroupName] = useState<string | null>(null);
  type InviteLogEntry = {
    id: string;
    sentAt?: string;
    status: "pending" | "sent" | "error";
  };
  const [members, setMembers] = useState<GroupMember[]>([]);
  const [invites, setInvites] = useState<GroupInvitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<GroupMember | null>(null);
  const [resendingIds, setResendingIds] = useState<Set<string>>(new Set());
  const [inviteLog, setInviteLog] = useState<Record<string, InviteLogEntry[]>>(
    {}
  );

  const loadMembers = useCallback(async () => {
    if (!id) {
      return;
    }
    try {
      setLoading(true);
      const [groupData, membersData, inviteData] = await Promise.all([
        groupAPI.getById(id),
        groupAPI.members.list(id),
        groupAPI.invitations.listGroupInvites(id),
      ]);
      setGroupName(groupData?.name ?? null);
      console.log("Loaded group members and invitations", {
        groupId: id,
        membersCount: membersData.length,
        invitesCount: inviteData.length,
      });
      setMembers(membersData);
      setInvites(inviteData);
      const seedLog: Record<string, InviteLogEntry[]> = {};
      inviteData.forEach((invite) => {
        if (invite.invitedAt) {
          seedLog[invite.id] = [
            {
              id: `${invite.id}-${invite.invitedAt}`,
              sentAt: invite.invitedAt,
              status: "sent",
            },
          ];
        }
      });
      const storedLog = inviteLogStorageKey
        ? localStorage.getItem(inviteLogStorageKey)
        : null;
      const parsedLog = storedLog
        ? (JSON.parse(storedLog) as Record<string, InviteLogEntry[]>)
        : {};
      const mergedLog = { ...parsedLog };
      Object.entries(seedLog).forEach(([inviteId, entries]) => {
        if (!mergedLog[inviteId]) {
          mergedLog[inviteId] = entries;
        }
      });
      setInviteLog(mergedLog);
    } catch (error) {
      console.error("Failed to load members:", error);
    } finally {
      setLoading(false);
    }
  }, [id, inviteLogStorageKey]);

  useEffect(() => {
    loadMembers();
  }, [loadMembers]);

  useEffect(() => {
    if (!inviteLogStorageKey) {
      return;
    }
    localStorage.setItem(inviteLogStorageKey, JSON.stringify(inviteLog));
  }, [inviteLog, inviteLogStorageKey]);

  const handleRoleChange = async (member: GroupMember, role: GroupRole) => {
    if (!id) {
      return;
    }
    console.log("Updating member role", {
      groupId: id,
      memberId: member.userId,
      role,
    });
    const updated = await groupAPI.members.update(id, member.userId, role);
    setMembers((prev) =>
      prev.map((item) => (item.id === updated.id ? updated : item))
    );
  };

  const handleRemove = (member: GroupMember) => {
    setRemoveTarget(member);
  };

  const confirmRemove = async (member: GroupMember) => {
    if (!id) {
      return;
    }
    console.log("Removing member", { groupId: id, memberId: member.userId });
    await groupAPI.members.remove(id, member.userId);
    setMembers((prev) => prev.filter((item) => item.id !== member.id));
    setRemoveTarget(null);
  };

  const handleInviteByEmail = async (
    email: string,
    role: Exclude<GroupRole, "owner">
  ) => {
    if (!id) {
      return;
    }
    const pendingId = `temp-${Date.now()}`;
    console.log("Sending group invitation", {
      groupId: id,
      email,
      role,
      pendingId,
    });
    setInvites((prev) => [
      ...prev,
      { id: pendingId, groupId: id, email, role },
    ]);
    setInviteLog((prev) => ({
      ...prev,
      [pendingId]: [{ id: pendingId, status: "pending" }],
    }));
    try {
      const invite = await groupAPI.invitations.send(id, { email, role });
      console.log("Sent group invitation", { groupId: id, invite });
      setInvites((prev) =>
        prev.map((item) => (item.id === pendingId ? invite : item))
      );
      setInviteLog((prev) => {
        const { [pendingId]: pendingEntries = [], ...rest } = prev;
        const updatedPending: InviteLogEntry[] = pendingEntries.map((entry) =>
          entry.id === pendingId
            ? {
                ...entry,
                status: "sent",
                sentAt: new Date().toISOString(),
                id: `${invite.id}-${Date.now()}`,
              }
            : entry
        );
        return {
          ...rest,
          [invite.id]: [...updatedPending, ...(prev[invite.id] || [])],
        };
      });
    } catch (error) {
      console.error("Failed to send invitation", { groupId: id, email, error });
      setInvites((prev) => prev.filter((item) => item.id !== pendingId));
      setInviteLog((prev) => {
        const { [pendingId]: _pendingEntries, ...rest } = prev;
        return rest;
      });
    }
  };

  const handleCancelInvite = async (invite: GroupInvitation) => {
    if (!id) {
      return;
    }
    console.log("Canceling invitation", { groupId: id, inviteId: invite.id });
    await groupAPI.invitations.cancel(id, invite.id);
    console.log("Canceled invitation", { groupId: id, inviteId: invite.id });
    setInvites((prev) => prev.filter((item) => item.id !== invite.id));
  };

  const handleResendInvite = async (invite: GroupInvitation) => {
    if (!id) {
      return;
    }
    console.log("Resending invitation", {
      groupId: id,
      inviteId: invite.id,
      email: invite.email,
    });
    const pendingLogId = `${invite.id}-${Date.now()}`;
    setResendingIds((prev) => new Set(prev).add(invite.id));
    setInviteLog((prev) => ({
      ...prev,
      [invite.id]: [
        { id: pendingLogId, status: "pending" },
        ...(prev[invite.id] || []),
      ],
    }));
    try {
      const updated = await groupAPI.invitations.resend(id, invite.id);
      console.log("Resent invitation", { groupId: id, invite: updated });
      setInvites((prev) =>
        prev.map((item) => (item.id === updated.id ? updated : item))
      );
      setInviteLog((prev) => ({
        ...prev,
        [updated.id]: (prev[updated.id] || []).map((entry) =>
          entry.id === pendingLogId
            ? {
                ...entry,
                status: "sent",
                sentAt: new Date().toISOString(),
              }
            : entry
        ),
      }));
    } catch (error) {
      console.error("Failed to resend invitation", {
        inviteId: invite.id,
        error,
      });
      setInviteLog((prev) => ({
        ...prev,
        [invite.id]: (prev[invite.id] || []).map((entry) =>
          entry.id === pendingLogId ? { ...entry, status: "error" } : entry
        ),
      }));
    } finally {
      setResendingIds((prev) => {
        const next = new Set(prev);
        next.delete(invite.id);
        return next;
      });
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 3 }}>
      <Stack spacing={3}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Stack spacing={0.5}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="h4" fontWeight={800}>
                {groupName || "Group members"}
              </Typography>
              {loading && !groupName && <CircularProgress size={18} />}
            </Stack>
          </Stack>
          <Tooltip title="Invite member">
            <IconButton
              color="primary"
              onClick={() => {
                console.log("Opening invite member modal");
                setInviteOpen(true);
              }}
            >
              <PersonAddAltIcon />
            </IconButton>
          </Tooltip>
        </Stack>

        {loading ? (
          <Typography color="text.secondary">Loading members...</Typography>
        ) : (
          <MembersList
            members={members}
            onRoleChange={handleRoleChange}
            onRemove={handleRemove}
          />
        )}

        <PendingInvitations
          invitations={invites}
          onCancel={handleCancelInvite}
          onResend={handleResendInvite}
          resendingIds={resendingIds}
          inviteLogs={inviteLog}
        />
      </Stack>

      <InviteMemberModal
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        onInviteByEmail={handleInviteByEmail}
      />

      <RemoveMemberConfirm
        open={Boolean(removeTarget)}
        member={removeTarget}
        onClose={() => setRemoveTarget(null)}
        onConfirm={confirmRemove}
      />
    </Container>
  );
};

export default GroupMembersPage;
